<template>
  <v-container
    fluid
    class="pa-4"
    style="background:#f4f6f9; min-height: 100vh;"
  >
    <!-- Header -->
    <v-row align="center" class="mb-4">
      <v-col cols="12" md="6"> </v-col>
      <v-col cols="12" md="6" class="text-md-right text-left">
        <v-btn-toggle
          v-model="datePreset"
          mandatory
          dense
          active-class="primary white--text"
          @change="fetchMetrics"
        >
          <v-btn value="7">Last 7 Days</v-btn>
          <v-btn value="30">Last 30 Days</v-btn>
          <v-btn value="all">All Time</v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row
      v-if="loading"
      justify="center"
      align="center"
      style="height: 300px;"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
        width="6"
      ></v-progress-circular>
    </v-row>

    <!-- Main Content -->
    <div v-else>
      <!-- Key Metrics Row -->
      <v-row>
        <v-col cols="12" md="6">
          <v-card class="customBorderRadius text-center py-4" elevation="1">
            <div
              class="text-subtitle-1 grey--text text--darken-1 font-weight-medium"
            >
              Total Leads
            </div>
            <div class="text-h3 font-weight-bold green--text">
              {{ metrics.totalLeads || 0 }}
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="customBorderRadius text-center py-4" elevation="1">
            <div
              class="text-subtitle-1 grey--text text--darken-1 font-weight-medium"
            >
              Conversion Rate
            </div>
            <div class="text-h3 font-weight-bold orange--text">
              {{ conversionRate }}%
            </div>
            <div class="text-caption grey--text">Leads converted from New</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Traffic Timeline -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card class="customBorderRadius pa-4" elevation="1">
            <v-card-title class="pa-0 mb-4 subtitle-2 font-weight-bold"
              >Traffic Over Time</v-card-title
            >
            <div style="height: 300px; width: 100%;">
              <line-chart
                v-if="hasTimeline"
                :chart-data="timelineData"
                :options="lineOptions"
                style="height:100%"
              />
              <div
                v-else
                class="d-flex align-center justify-center grey--text"
                style="height: 100%;"
              >
                No traffic data available for this period.
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Source / Campaign / Geography -->
      <v-row class="mt-4">
        <v-col cols="12" md="4">
          <v-card class="customBorderRadius pa-4 h-100" elevation="1">
            <v-card-title class="pa-0 mb-4 subtitle-2 font-weight-bold"
              >Traffic Sources</v-card-title
            >
            <div style="height: 250px;">
              <pie-chart
                v-if="hasSources"
                :chart-data="sourceData"
                :options="pieOptions"
                style="height:100%"
              />
              <div
                v-else
                class="d-flex align-center justify-center grey--text"
                style="height: 100%;"
              >
                No source data
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="customBorderRadius pa-4 h-100" elevation="1">
            <v-card-title class="pa-0 mb-4 subtitle-2 font-weight-bold"
              >Top Ad Campaigns</v-card-title
            >
            <div style="height: 250px;">
              <bar-chart
                v-if="hasCampaigns"
                :chart-data="campaignData"
                :options="barOptions"
                style="height:100%"
              />
              <div
                v-else
                class="d-flex align-center justify-center grey--text"
                style="height: 100%;"
              >
                No campaign data
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="customBorderRadius pa-4 h-100" elevation="1">
            <v-card-title class="pa-0 mb-4 subtitle-2 font-weight-bold"
              >Top Locations</v-card-title
            >
            <div style="height: 250px; overflow-y: auto;">
              <v-list
                v-if="metrics.geography && metrics.geography.length > 0"
                dense
                class="pa-0"
              >
                <v-list-item
                  v-for="(loc, i) in metrics.geography.slice(0, 10)"
                  :key="i"
                  class="px-0"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ loc.location }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip small color="primary" outlined
                      >{{ loc.count }} views</v-chip
                    >
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <div
                v-else
                class="d-flex align-center justify-center grey--text"
                style="height: 100%;"
              >
                No location data
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Funnel & Devices -->
      <v-row class="mt-4">
        <v-col cols="12" md="6">
          <v-card class="customBorderRadius pa-4 h-100" elevation="1">
            <v-card-title class="pa-0 mb-4 subtitle-2 font-weight-bold"
              >CRM Funnel (Dispositions)</v-card-title
            >
            <div style="height: 300px;">
              <pie-chart
                v-if="hasDispositions"
                :chart-data="dispositionData"
                :options="pieOptions"
                style="height:100%"
              />
              <div
                v-else
                class="d-flex align-center justify-center grey--text"
                style="height: 100%;"
              >
                No disposition data available (List may be empty)
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="customBorderRadius pa-4 h-100" elevation="1">
            <v-card-title class="pa-0 mb-4 subtitle-2 font-weight-bold"
              >Devices</v-card-title
            >
            <div style="height: 300px;">
              <pie-chart
                v-if="hasDevices"
                :chart-data="deviceData"
                :options="pieOptions"
                style="height:100%"
              />
              <div
                v-else
                class="d-flex align-center justify-center grey--text"
                style="height: 100%;"
              >
                No device data
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import LineChart from "../js/linechart.js";
import PieChart from "../js/pie.js";
import BarChart from "../js/bar.js";
import fb from "../js/firebase.js";

export default {
  name: "GoPagesV2Analytics",
  components: {
    LineChart,
    PieChart,
    BarChart,
  },
  data() {
    return {
      loading: true,
      datePreset: "all",
      metrics: {
        totalViews: 0,
        totalLeads: 0,
        viewsByDate: [],
        sources: [],
        campaigns: [],
        geography: [],
        dispositions: [],
        devices: [],
        directViews: 0,
      },
      pieOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: "right", labels: { boxWidth: 12 } },
      },
      lineOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{ gridLines: { display: false } }],
          yAxes: [
            {
              gridLines: { color: "#f0f0f0" },
              ticks: { beginAtZero: true, precision: 0 },
            },
          ],
        },
      },
      barOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{ gridLines: { display: false } }],
          yAxes: [
            {
              gridLines: { color: "#f0f0f0" },
              ticks: { beginAtZero: true, precision: 0 },
            },
          ],
        },
      },
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT"]),
    conversionRate() {
      if (!this.metrics.totalLeads) return "0.0";
      const newCount = (this.metrics.dispositions || []).find(
        (d) => d.name === "New" || d.name === "new",
      );
      const newLeads = newCount ? newCount.count : 0;
      const converted = this.metrics.totalLeads - newLeads;
      return ((converted / this.metrics.totalLeads) * 100).toFixed(1);
    },
    hasTimeline() {
      return this.metrics.viewsByDate && this.metrics.viewsByDate.length > 0;
    },
    timelineData() {
      if (!this.hasTimeline) return null;
      return {
        labels: this.metrics.viewsByDate.map((d) => d.date),
        datasets: [
          {
            label: "Views",
            data: this.metrics.viewsByDate.map((d) => d.count),
            borderColor: "#1976D2",
            backgroundColor: "rgba(25, 118, 210, 0.1)",
            borderWidth: 2,
            pointRadius: 4,
            pointBackgroundColor: "#1976D2",
            fill: true,
            lineTension: 0.3,
          },
        ],
      };
    },
    hasSources() {
      return (
        (this.metrics.sources && this.metrics.sources.length > 0) ||
        this.metrics.directViews > 0
      );
    },
    sourceData() {
      if (!this.hasSources) return null;
      let labels = [];
      let data = [];
      let predefinedColors = [
        "#4CAF50",
        "#2196F3",
        "#FFC107",
        "#E91E63",
        "#9C27B0",
        "#00BCD4",
      ];
      let bgColors = [];

      this.metrics.sources.forEach((s, idx) => {
        labels.push(s.source);
        data.push(s.count);
        bgColors.push(predefinedColors[idx % predefinedColors.length]);
      });

      if (this.metrics.directViews > 0) {
        labels.push("Direct/Unknown");
        data.push(this.metrics.directViews);
        bgColors.push("#9E9E9E");
      }

      return {
        labels,
        datasets: [{ data, backgroundColor: bgColors }],
      };
    },
    hasCampaigns() {
      return this.metrics.campaigns && this.metrics.campaigns.length > 0;
    },
    campaignData() {
      if (!this.hasCampaigns) return null;
      return {
        labels: this.metrics.campaigns.map((c) => c.campaign),
        datasets: [
          {
            label: "Campaign Views",
            data: this.metrics.campaigns.map((c) => c.count),
            backgroundColor: "#FF9800",
          },
        ],
      };
    },
    hasDispositions() {
      return this.metrics.dispositions && this.metrics.dispositions.length > 0;
    },
    dispositionData() {
      if (!this.hasDispositions) return null;
      document.body.style.setProperty("--v-primary-base", "#1976D2");
      let predefinedColors = [
        "#2E7D32",
        "#EF6C00",
        "#C62828",
        "#1565C0",
        "#6A1B9A",
      ];
      return {
        labels: this.metrics.dispositions.map((d) => d.name),
        datasets: [
          {
            data: this.metrics.dispositions.map((d) => d.count),
            backgroundColor: this.metrics.dispositions.map(
              (_, i) => predefinedColors[i % predefinedColors.length],
            ),
          },
        ],
      };
    },
    hasDevices() {
      return this.metrics.devices && this.metrics.devices.length > 0;
    },
    deviceData() {
      if (!this.hasDevices) return null;
      return {
        labels: this.metrics.devices.map((d) => d.device),
        datasets: [
          {
            data: this.metrics.devices.map((d) => d.count),
            backgroundColor: ["#00ACC1", "#FF5722", "#607D8B"],
          },
        ],
      };
    },
  },
  mounted() {
    fb.log("gopage_analytics_viewed", { pageId: this.$route.params.id });
    this.fetchMetrics();
  },
  methods: {
    getDateRange() {
      if (this.datePreset === "all") return null;
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - parseInt(this.datePreset, 10));
      return {
        start: start.toISOString(),
        end: end.toISOString(),
      };
    },
    async fetchMetrics() {
      this.loading = true;
      const endpoint = `${this.ENDPOINT}/gopages-analytics/metrics`;
      try {
        let payload = { pageId: this.$route.params.id };
        let dateRange = this.getDateRange();
        if (dateRange) payload.dateRange = dateRange;

        const resp = await this.$http.post(endpoint, payload);
        if (resp && resp.body) {
          this.metrics = resp.body;
        } else {
          throw new Error("Unable to fetch GoPages analytics metrics");
        }
      } catch (err) {
        console.error("Failed to fetch metrics", err);
        this.$swal({
          type: "warning",
          text:
            "Unable to load analytics right now. Please try again in a moment.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.customBorderRadius {
  border-radius: 12px !important;
}
.h-100 {
  height: 100%;
}
</style>
